/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { merge } from "lodash/fp";
import { deserialize } from "./deserialize-jsonapi";
import { encodeQueryToURL } from "@marapp/earth-shared";

export interface RequestQuery {
  [key: string]: any;
}
export type RequestMethod = "get" | "post" | "put" | "delete";
/** The request options the apps' `BaseAPIService.request` honours. */
export interface RequestConfigBase {
  query?: RequestQuery;
  method?: RequestMethod;
  data?: any;
}
export interface RequestConfig extends RequestConfigBase {
  timeout?: number;
  headers?: Record<string, string>;
}

const TIMEOUT = 30000;

export const BaseAPIService = {
  /**
   * Creates an axios request based on path and request options.
   * @param path: identifies the specific resource in the host
   * @param config
   * @param deserializer: JSON:API deserializer
   */
  request: (
    path: string,
    config: RequestConfig,
    deserializer: (response: AxiosResponse) => any = BaseAPIService.deserialize
  ) => {
    const defaults: RequestConfig = {
      method: "get",
      data: {},
      timeout: TIMEOUT,
    };

    const params: RequestConfig = merge(defaults, config);

    const options: AxiosRequestConfig = {
      url: path,
      method: params.method,
      data: params.data,
      timeout: params.timeout,
      headers: params.headers,
    };

    return axios
      .request(options)
      .then((response) => deserializer(response))
      .catch((error) => {
        const errorToThrow = error?.response?.data?.data
          ? deserializer(error?.response)
          : error?.response;

        throw errorToThrow || error; // in the case of connection errors, error.response is missing
      });
  },
  /**
   * JSON:API deserializer
   * @param response
   */
  deserialize: (response: AxiosResponse): any => {
    return deserialize(response?.data);
  },
};

/**
 * JSON:API meta deserializer
 * @param response
 */
export const metaDeserializer = (response: AxiosResponse): any => {
  return {
    data: deserialize(response?.data),
    meta: response?.data?.meta,
  };
};

/**
 * Generate a cache key based on URL path and query params.
 * @param path
 * @param query
 */
export const generateCacheKey = (path: string, query: RequestQuery): string => {
  return encodeQueryToURL(path, query);
};
